import { FormattedWorkHistory, WorkLocation } from "./TalentWorkHistory";
import { WorkHistory } from "./types/queries";

export const isExistingWorkItem = (
  item: WorkHistory,
  work: FormattedWorkHistory,
) => {
  // Compare isEezyWork, if they are not equal, return false
  if (item.isEezyWork !== work.isEezyWork) return false;
  // Compare workTitleIds if they exist
  if (item.workTitleId && work.workTitleId) {
    return work.workTitleId === item.workTitleId;
  }
  // Compare workTitleNames if they exist
  if (item.workTitleName && work.workTitleName) {
    return work.workTitleName === item.workTitleName;
  }
  // Note: above workTitleName comparison makes it possible to put item with workTitleId and one without together based on workTitleName
  // e.g. {workTitleId: "132", workTitleName: "Kodinkonekorjaaja"} and {workTitleId: null, workTitleName: "Kodinkonekorjaaja"} will be put together
  return false;
};

export const getLongestDateRange = (workLocations: WorkLocation[]) => {
  let earliestStartDate: string = "";
  let latestEndDate: string | null = "";

  // Takes in a date string and returns milliseconds
  const getTimeMs = (date: string) => new Date(date).getTime();

  // Find earliestStartDate
  workLocations.forEach((workLocation) => {
    if (!earliestStartDate) {
      earliestStartDate = workLocation.startDate;
      return;
    }
    if (getTimeMs(workLocation.startDate) < getTimeMs(earliestStartDate)) {
      earliestStartDate = workLocation.startDate;
    }
  });

  // Find latestEndDate
  workLocations.forEach((workLocation) => {
    // If already found null, no need to check anymore
    if (latestEndDate === null) return;
    // Check if endDate is null
    if (!workLocation.endDate) {
      latestEndDate = null;
      return;
    }
    // If latestEndDate doesn't exist and endDate is not null, set endDate as latestEndDate
    if (!latestEndDate && workLocation.endDate) {
      latestEndDate = workLocation.endDate;
      return;
    }
    // If endDate is not null, check if it is the latest
    if (
      workLocation.endDate &&
      getTimeMs(workLocation.endDate) > getTimeMs(latestEndDate)
    ) {
      latestEndDate = workLocation.endDate;
    }
  });

  return { startDate: earliestStartDate, endDate: latestEndDate };
};

export const getWorkLocationObject = (item: WorkHistory) => {
  return {
    workHistoryId: item.id,
    ...(item.workLocationName && {
      workLocationName: item.workLocationName,
    }),
    ...(item.workTitleId && { workTitleId: item.workTitleId }),
    ...(item.customerOrgName && {
      customerOrgName: item.customerOrgName,
    }),
    ...(item.jobDescription && {
      jobDescription: item.jobDescription,
    }),
    startDate: item.startDate as string,
    endDate: item.endDate as string,
    isEezyWork: item.isEezyWork,
    editable: item.editable,
    deletable: item.deletable,
  };
};

// Utility function to compare dates
const compareDates = (dateA: string, dateB: string): number => {
  const a = new Date(dateA);
  const b = new Date(dateB);
  return a > b ? -1 : a < b ? 1 : 0;
};

const sortWorkTitlesByDates = (data: FormattedWorkHistory[]) => {
  // Sort data array by startDate and endDate
  return data.sort((a, b) => {
    // Sort by startDate if both have missing or provided endDate
    if ((!a.endDate && !b.endDate) || (a.endDate && b.endDate)) {
      return compareDates(a.startDate, b.startDate);
    }

    // Prioritize records with missing endDate
    return !a.endDate ? -1 : 1;
  });
};

// Sort workLocations within each object
const sortWorkLocations = (data: FormattedWorkHistory[]) => {
  const sortedDataArray = data.map((item) => {
    if (item.workLocations?.length > 1) {
      const sortedLocations = [...item.workLocations].sort((locA, locB) => {
        const result = compareDates(locA.startDate, locB.startDate);
        return result !== 0
          ? result
          : locA.endDate && locB.endDate
            ? compareDates(locA.endDate, locB.endDate)
            : 0;
      });

      return { ...item, workLocations: sortedLocations };
    }
    return item;
  });
  return sortedDataArray;
};

export const sortWorkHistory = (data: FormattedWorkHistory[]) => {
  const sortedDataArray = sortWorkTitlesByDates(data);
  return sortWorkLocations(sortedDataArray);
};
